import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { http } from '../../lib/api';
import { AppLayout } from '../../components/layout';
import { Button, Card, StatusBadge, Input } from '../../components/core';
import { DataTable, type Column, Pagination, Modal, ConfirmDialog, SpinnerBlock } from '../../components/feedback';
import { ADMIN_NAV } from './nav';

interface TenantRow {
  id: string; name: string; slug: string; status: string;
  ownerEmail: string | null; planName: string | null;
  subscriptionStatus: string | null; streamCount: number; createdAt: string;
}
interface PageResp<T> { items: T[]; total: number; page: number; pageSize: number; totalPages: number; }

function initials(name: string) {
  const p = name.replace(/[._-]+/g, ' ').trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
}

export function AdminTenantsPage() {
  const [rows, setRows] = useState<TenantRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', planId: '' });
  const [plans, setPlans] = useState<{ id: string; name: string; priceMonth: number; status: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TenantRow | null>(null);
  const [editTenant, setEditTenant] = useState<TenantRow | null>(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [editSaving, setEditSaving] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    http.get<PageResp<TenantRow>>('/api/admin/tenants', {
      page, pageSize: 20, search: search || undefined, status: statusFilter || undefined,
    }).then((d) => { setRows(d.items); setTotalPages(d.totalPages); })
      .catch((e) => setError(e.message ?? 'Failed')).finally(() => setLoading(false));
  };

  useEffect(load, [page, statusFilter]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => {
    http.get<{ id: string; name: string; priceMonth: number; status: string }[]>('/api/admin/plans')
      .then((list) => setPlans(list.filter((p) => p.status !== 'ARCHIVED')))
      .catch(() => { /* plans dropdown is non-critical */ });
  }, []);

  const createTenant = async () => {
    if (!form.name || !form.email || !form.password) return;
    setSaving(true);
    try {
      // planId omitted ⇒ tenant is created without a subscription and cannot
      // create streams until it subscribes itself.
      const payload: Record<string, unknown> = {
        name: form.name, email: form.email, phone: form.phone, password: form.password,
      };
      if (form.planId) payload.planId = form.planId;
      await http.post('/api/admin/tenants', payload);
      setShowCreate(false); setForm({ name: '', email: '', phone: '', password: '', planId: '' }); setPage(1); load();
    } catch (e) { setError((e as Error).message ?? 'Create failed'); }
    setSaving(false);
  };

  const applyStatus = async (id: string, currentStatus: string) => {
    if (actingId === id) return;
    const suspend = currentStatus === 'ACTIVE';
    setActingId(id);
    try {
      await http.post(`/api/admin/tenants/${id}/${suspend ? 'suspend' : 'activate'}`, {});
      const next = suspend ? 'SUSPENDED' : 'ACTIVE';
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: next } : r)));
    } catch (e) { setError((e as Error).message ?? 'Update failed'); }
    setActingId(null);
  };

  const deleteTenant = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setActingId(id);
    try {
      await http.delete(`/api/admin/tenants/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setConfirmDelete(null);
      if (page > 1 && rows.length === 1) setPage(page - 1);
    } catch (e) { setError((e as Error).message ?? 'Delete failed'); }
    setActingId(null);
  };

  const openEdit = (t: TenantRow) => {
    setEditTenant(t);
    setEditForm({ name: t.name, email: t.ownerEmail ?? '', phone: '', password: '' });
  };

  const saveEdit = async () => {
    if (!editTenant || !editForm.name.trim()) return;
    setEditSaving(true);
    try {
      const payload: Record<string, string> = { name: editForm.name.trim() };
      if (editForm.email.trim()) payload.email = editForm.email.trim();
      if (editForm.phone.trim()) payload.phone = editForm.phone.trim();
      if (editForm.password.trim()) payload.password = editForm.password.trim();
      await http.patch(`/api/admin/tenants/${editTenant.id}`, payload);
      setRows((prev) => prev.map((r) => (r.id === editTenant.id ? { ...r, name: editForm.name.trim(), ownerEmail: editForm.email.trim() || r.ownerEmail } : r)));
      setEditTenant(null);
      setEditForm({ name: '', email: '', phone: '', password: '' });
    } catch (e) { setError((e as Error).message ?? 'Update failed'); }
    setEditSaving(false);
  };

  const columns: Column<TenantRow>[] = [
    { key: 'name', header: 'Tenant', render: (t) => (
      <button className="tenant-cell tenant-link" onClick={() => navigate(`/admin/tenants/${t.id}`)} title="View tenant details">
        <span className="tenant-avatar">{initials(t.name)}</span>
        <div><div className="tenant-name">{t.name}</div><div className="tenant-slug">{t.slug}</div></div>
      </button>
    )},
    { key: 'owner', header: 'Owner', render: (t) => <span className="text-sm">{t.ownerEmail ?? '—'}</span> },
    { key: 'plan', header: 'Plan', render: (t) => t.planName
      ? <span className="count-pill">{t.planName}</span>
      : <span className="text-muted text-sm">None</span> },
    { key: 'status', header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
    { key: 'streams', header: 'Streams', render: (t) => <span className="font-medium text-sm">{t.streamCount}</span> },
    { key: 'created', header: 'Created', render: (t) => (
      <span className="text-muted text-sm">{new Date(t.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
    )},
    { key: 'actions', header: 'Actions', render: (t) => {
      const suspend = t.status === 'ACTIVE';
      const busy = actingId === t.id;
      return (
        <div className="row-actions">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/tenants/${t.id}`)} title="View tenant details">View</Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => openEdit(t)} title="Edit tenant">Edit</Button>
          <Button variant={suspend ? 'secondary' : 'primary'} size="sm" disabled={busy} onClick={() => void applyStatus(t.id, t.status)}>
            {busy ? (suspend ? 'Suspending…' : 'Activating…') : (suspend ? 'Suspend' : 'Activate')}
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmDelete(t)} title="Delete tenant">Delete</Button>
        </div>
      );
    } },
  ];

  return (
    <AppLayout brand="LiveStreamX" navGroups={ADMIN_NAV} variant="admin">
      <div className="admin-page">
        <div className="page-header admin-page-header">
          <div><h1 className="page-title">Tenants</h1><p className="page-subtitle">Manage organizations and accounts</p></div>
          <Button onClick={() => setShowCreate(true)}>+ New Tenant</Button>
        </div>
        {error && <div className="alert danger">{error}</div>}
        <div className="toolbar">
          <div className="field grow">
            <input className="input" placeholder="Search tenants…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="field" style={{ minWidth: 160 }}>
            <select className="input" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
          </div>
        </div>
        {loading ? <SpinnerBlock /> : (
          <Card>
            <DataTable columns={columns} rows={rows} />
            {!rows.length && <p className="text-muted text-sm" style={{ margin: 0 }}>No tenants found.</p>}
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </Card>
        )}
        {showCreate && (
          <Modal title="Create Tenant" onClose={() => setShowCreate(false)} footer={<>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createTenant} disabled={saving}>{saving ? 'Creating…' : 'Create'}</Button>
          </>}>
            <div className="modal-grid">
              <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
              <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} hint="Optional" />
              <Input label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required hint="Min 8 characters" />
              <div className="field">
                <label htmlFor="plan">Plan</label>
                <select id="plan" className="input" value={form.planId} onChange={(e) => setForm({ ...form, planId: e.target.value })}>
                  <option value="">No plan — tenant must subscribe</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — ₹{p.priceMonth.toLocaleString('en-IN')}/mo</option>)}
                  {!plans.length && <option disabled>No plans available</option>}
                </select>
                <div className="text-muted text-xs">Without a plan the tenant cannot create streams until it subscribes and pays on the Billing page.</div>
              </div>
            </div>
          </Modal>
        )}
        {confirmDelete && (
          <ConfirmDialog
            title="Delete Tenant"
            body={`Delete "${confirmDelete.name}"? This permanently removes the tenant, its members, streams and billing data. This cannot be undone.`}
            confirmLabel="Delete Tenant"
            onCancel={() => setConfirmDelete(null)}
            onConfirm={() => void deleteTenant()}
          />
        )}
        {editTenant && (
          <Modal title="Edit Tenant" onClose={() => setEditTenant(null)} footer={<>
            <Button variant="secondary" onClick={() => setEditTenant(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save'}</Button>
          </>}>
            <div className="modal-grid">
              <Input label="Name" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} required />
              <Input label="Email" type="email" value={editForm.email} onChange={(v) => setEditForm({ ...editForm, email: v })} required />
              <Input label="Phone" value={editForm.phone} onChange={(v) => setEditForm({ ...editForm, phone: v })} hint="Optional" />
              <Input label="Password" type="password" value={editForm.password} onChange={(v) => setEditForm({ ...editForm, password: v })} hint="Leave blank to keep current" />
            </div>
          </Modal>
        )}
      </div>
    </AppLayout>
  );
}
